import axios from 'axios';
import { Cookies } from 'react-cookie';
import { atom, RecoilState, SetterOrUpdater, useRecoilValue } from 'recoil';
import { URL_FOUNDATION } from '../config/keys';
import * as React from 'react';
import { AGroups } from './groups';
import { ObjectAction } from '../web-types/objectActions';
import { AProfile } from './global/users';

type AllowDenyListRaw = {
	target: ObjectAction,
	target_id?: number,
	target_uuid?: string,
	deny: boolean,
	users: Array<number>,
	groups: Array<number>,
	created_at: string,
	updated_at: string,
}

export type AllowDenyList = AllowDenyListRaw & {
	id: number,
}

export type PostAllowDenyList = AllowDenyListRaw & Partial<AllowDenyList>

type RawAllowDenyList<T> = {
	globalDenyList?: T | undefined,
	globalAllowList?: T | undefined,
	localDenyList?: T | undefined,
	localAllowList?: T | undefined,
}

export type AppliedAllowDenyList = RawAllowDenyList<AllowDenyList>

export type AppliedPostAllowDenyList = RawAllowDenyList<PostAllowDenyList>

// -----------------------[ ATOM ]----------------------- //

export const AAllowDenyList: RecoilState<AllowDenyList[]> = atom({
	key: 'atom_allow_deny_list', // UNIQUE ID
	default: getAllowDenyList()
});

// -----------------------[ HOOKS ]----------------------- //

// Rules:
// - if target_id and target_uuid are undefined, that means that the allow/deny list applies to all targets of the given type
// - if user is denied / allowed in the global allow/deny list, it is overridden by the local allow/deny list
// - if the user is denied AND allowed, the user is denied
export function canUserAccess() {
	const profile = useRecoilValue(AProfile);
	const userId = profile.id;
	const lst = useRecoilValue(AAllowDenyList);
	const groupWithMembers = useRecoilValue(AGroups);
	const groupMap = React.useMemo(() => new Map(groupWithMembers.map(g => [g.id, g.user_ids ?? []])), [groupWithMembers]);

	return React.useCallback<(objectActions: ObjectAction, targetId?: number, targetUuid?: string) => boolean>((objectActions, targetId, targetUuid) => {
		const globalDenyList = lst.find(item => item.target === objectActions && !item.target_id && !item.target_uuid && item.deny);
		const localDenyList = lst.find(item => item.target === objectActions && item.target_id == targetId && item.target_uuid == targetUuid && item.deny);
		const localAllowList = lst.find(item => item.target === objectActions && item.target_id == targetId && item.target_uuid == targetUuid && !item.deny);

		if (localDenyList?.users?.includes(userId) || localDenyList?.groups?.flatMap((group) => groupMap.get(group) ?? []).includes(userId)) return false;
		if (localAllowList?.users?.includes(userId) || localAllowList?.groups?.flatMap((group) => groupMap.get(group) ?? []).includes(userId)) return true;
		if (globalDenyList?.users?.includes(userId) || globalDenyList?.groups?.flatMap((group) => groupMap.get(group) ?? []).includes(userId)) return false;
		return true;
	}, [lst, groupMap]);
}

export function useAllowDenyList(objectActions: ObjectAction[], targetId?: number, targetUuid?: string): Map<ObjectAction, AppliedAllowDenyList> {
	const lst = useRecoilValue(AAllowDenyList);

	return React.useMemo(() => objectActions.reduce((map, oa) => {
		const globalDenyList = lst.find(item => item.target === oa && !item.target_id && !item.target_uuid && item.deny);
		const globalAllowList = lst.find(item => item.target === oa && !item.target_id && !item.target_uuid && !item.deny);
		const localDenyList = lst.find(item => item.target === oa && item.target_id == targetId && item.target_uuid == targetUuid && item.deny);
		const localAllowList = lst.find(item => item.target === oa && item.target_id == targetId && item.target_uuid == targetUuid && !item.deny);

		map.set(oa, {
			globalDenyList,
			globalAllowList,
			localDenyList,
			localAllowList,
		});
		return map;
	}, new Map()), [lst, objectActions, targetId, targetUuid]);
}

// -----------------------[ API ]------------------------ //
const cookies = new Cookies();
const token: string | null = cookies.get('id_token') || null;

axios.defaults.headers.common.Authorization = `${token ?? ''}`;

export async function getAllowDenyList(): Promise<AllowDenyList[]> {
	try {
		return await axios({
			method: 'get',
			url: `${URL_FOUNDATION}/api/v2/allow-deny-list`
		}).then(res => res.data);
	} catch (e) {
		console.error(e);
		return [];
	}
}

export async function postAllowDenyList(data: PostAllowDenyList, setAtom: SetterOrUpdater<AllowDenyList[]>): Promise<AllowDenyList> {
	const newItem = await axios.post<AllowDenyList>(`${URL_FOUNDATION}/api/v2/allow-deny-list`, data).then(res => res.data);

	setAtom((prev) => {
		const index = prev.findIndex((g) => g.id == newItem.id);
		if (index !== -1) {
			const res = [...prev];
			res[index] = newItem;
			return res;
		}
		return [...prev, newItem];
	});

	return newItem;
}

export async function postAllowDenyListMult(data: PostAllowDenyList[], setAtom: SetterOrUpdater<AllowDenyList[]>): Promise<AllowDenyList[]> {
	const newItems = await axios.post<AllowDenyList[]>(`${URL_FOUNDATION}/api/v2/allow-deny-list/multiple`, data).then(res => res.data);

	setAtom((prev) => {
		const res = [...prev];
		for (const newItem of newItems) {
			const index = res.findIndex((g) => g.id == newItem.id);
			if (index !== -1) {
				res[index] = newItem;
			} else {
				res.push(newItem);
			}
		}
		return res;
	});
	return newItems;
}

export async function deleteAllowDenyList(id: number, setAtom: SetterOrUpdater<AllowDenyList[]>): Promise<void> {
	try {
		await axios.delete(`${URL_FOUNDATION}/api/v2/allow-deny-list/${id}`);
		setAtom((prev) => {
			const index = prev.findIndex((g) => g.id == id);
			if (index !== -1) {
				const res = [...prev];
				res.splice(index, 1);
				return res;
			}
			return prev;
		});
	} catch (e) {
		console.error(e);
	}
}
